import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

/**
 * Role-aware confirmation dialog for project deletion / archiving.
 *
 * mode:
 *   'active-contributor'  — Contributor deleting an active project; archive only
 *   'active-privileged'   — Admin/Super User deleting an active project; archive or permanent delete
 *   'archived-privileged' — Admin/Super User permanently deleting an already-archived project
 *   'restore'             — Any authorized user restoring an archived project
 */
const DeleteConfirmationDialog = ({
  open,
  onClose,
  onArchive,
  onDeletePermanently,
  onRestore,
  project,
  mode,
}) => {
  const projectName = project?.title || project?.project_title || 'this project';

  const actionsSx = {
    px: 3,
    pb: 2.5,
    pt: 1.5,
    gap: 1,
    justifyContent: 'center',
  };

  const btnSx = {
    minWidth: 120,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
  };

  return (
    <Dialog
      sx={{ '& .MuiDialog-paper': { width: '560px' } }}
      maxWidth="sm"
      open={open}
      onClose={onClose}
    >
      {mode === 'archived-privileged' ? (
        <>
          <DialogTitle>Permanently Delete Project?</DialogTitle>
          <DialogContent dividers>
            <DialogContentText>
              Are you sure you want to permanently delete <b>{projectName}</b>?
            </DialogContentText>
            <br />
            <DialogContentText>
              This action cannot be undone. All project data will be permanently removed.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={actionsSx}>
            <Button autoFocus onClick={onClose} variant="text" sx={btnSx}>
              Cancel
            </Button>
            <Button color="error" variant="contained" onClick={onDeletePermanently} sx={btnSx}>
              Delete Permanently
            </Button>
          </DialogActions>
        </>
      ) : mode === 'active-privileged' ? (
        <>
          <DialogTitle>Delete Project</DialogTitle>
          <DialogContent dividers>
            <DialogContentText>
              What would you like to do with <b>{projectName}</b>?
            </DialogContentText>
            <br />
            <DialogContentText>
              <strong>Archive (30 days):</strong> The project will be moved to the Archived section
              and permanently deleted after 30 days. You can restore it at any time before then.
            </DialogContentText>
            <br />
            <DialogContentText>
              <strong>Delete Permanently:</strong> The project and all its data will be immediately
              and permanently removed. This cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={actionsSx}>
            <Button autoFocus onClick={onClose} variant="text" sx={btnSx}>
              Cancel
            </Button>
            <Button color="warning" variant="outlined" onClick={onArchive} sx={btnSx}>
              Archive (30 days)
            </Button>
            <Button color="error" variant="contained" onClick={onDeletePermanently} sx={btnSx}>
              Delete Permanently
            </Button>
          </DialogActions>
        </>
      ) : mode === 'restore' ? (
        <>
          <DialogTitle>Restore Project?</DialogTitle>
          <DialogContent dividers>
            <DialogContentText>
              Restore <b>{projectName}</b> to your active projects?
            </DialogContentText>
            <br />
            <DialogContentText>
              The project will be moved back to your active projects list and will be fully accessible again.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={actionsSx}>
            <Button autoFocus onClick={onClose} variant="text" sx={btnSx}>
              Cancel
            </Button>
            <Button color="success" variant="contained" onClick={onRestore} sx={btnSx}>
              Restore
            </Button>
          </DialogActions>
        </>
      ) : (
        /* mode === 'active-contributor' */
        <>
          <DialogTitle>Archive Project?</DialogTitle>
          <DialogContent dividers>
            <DialogContentText>
              Are you sure you want to archive <b>{projectName}</b>?
            </DialogContentText>
            <br />
            <DialogContentText>
              The project will be recoverable for 30 days. After 30 days it will be permanently
              deleted.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={actionsSx}>
            <Button autoFocus onClick={onClose} variant="text" sx={btnSx}>
              Cancel
            </Button>
            <Button color="warning" variant="outlined" onClick={onArchive} sx={btnSx}>
              Archive (30 days)
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default DeleteConfirmationDialog;
